import React, { useState, useEffect, useCallback } from "react";
import { X } from "lucide-react";
import { masterService, categoryService, unitService } from "../../../api/masterService";
import { useNotification } from "../../../contexts/NotificationContext";
import SearchableSelect from "../../../components/SearchableSelect";
import BarcodeScanInput from "../../../components/BarcodeScanInput";
import { formatNumberWithSeparator, parseNumberFromSeparator } from "../../../utils/numberFormat";

export default function EditProductModal({ isOpen, onClose, product, onSuccess }) {
  const { showSuccess, showError } = useNotification();
  const isApps = localStorage.getItem("PREVIEW_APPS") === "true";
  const [formData, setFormData] = useState({
    name: product?.name || "",
    description: product?.description || "",
    purchase_price: product?.purchase_price || 0,
    sell_price: product?.sell_price || 0,
    category_id: product?.category_id || "",
    barcode: product?.barcode || "",
    sku: product?.sku || "",
    status: product?.status || "ACTIVE",
    is_track_stock: product?.is_track_stock || false,
    unit_id: product?.unit_id || "",
    supplier: product?.supplier || "",
    min_stock: product?.min_stock || 0,
    rack_location: product?.rack_location || "",
    weight: product?.weight || 0,
    dimensions: product?.dimensions || "",
    material: product?.material || "",
    warranty_months: product?.warranty_months || 0,
    tags: product?.tags || [],
    notes: product?.notes || ""
  });
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);

  const handleAddCategory = async (name) => {
    try {
      const res = await categoryService.create({ name });
      const newCat = res.data?.data || res.data;
      if (newCat && newCat.id) {
        setCategories((prev) => [newCat, ...prev]);
        setFormData((prev) => ({ ...prev, category_id: newCat.id }));
        showSuccess(`Kategori "${newCat.name}" berhasil ditambahkan`);
        return newCat;
      }
    } catch (err) {
      showError(err.message || "Gagal menambah kategori");
      throw err;
    }
  };

  const handleAddUnit = async (name) => {
    try {
      const unitCode = name.toUpperCase().replace(/\s+/g, "_");
      const res = await unitService.create({ name, code: unitCode });
      const newUnit = res.data?.data || res.data;
      if (newUnit && newUnit.id) {
        setUnits((prev) => [newUnit, ...prev]);
        setFormData((prev) => ({ ...prev, unit_id: newUnit.id }));
        showSuccess(`Satuan "${newUnit.name}" berhasil ditambahkan`);
        return newUnit;
      }
    } catch (err) {
      showError(err.message || "Gagal menambah satuan");
      throw err;
    }
  };

  const fetchCategories = useCallback(async (search = "") => {
    try {
      const res = await categoryService.list({ search });
      const items = res.data?.items || res.data?.data || res.data || [];
      return items;
    } catch (error) {
      console.error("Failed to fetch categories");
      return [];
    }
  }, []);

  const fetchUnits = useCallback(async (search = "") => {
    try {
      const res = await unitService.list({ search });
      const items = res.data?.items || res.data?.data || res.data || [];
      return items;
    } catch (error) {
      console.error("Failed to fetch units");
      return [];
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      if (product) {
        setFormData({
          name: product.name || "",
          description: product.description || "",
          purchase_price: product.purchase_price || 0,
          sell_price: product.sell_price || 0,
          category_id: product.category_id || "",
          barcode: product.barcode || "",
          sku: product.sku || "",
          status: product.status || "ACTIVE",
          is_track_stock: product.is_track_stock || false,
          unit_id: product.unit_id || "",
          supplier: product.supplier || "",
          min_stock: product.min_stock || 0,
          rack_location: product.rack_location || "",
          weight: product.weight || 0,
          dimensions: product.dimensions || "",
          material: product.material || "",
          warranty_months: product.warranty_months || 0,
          tags: product.tags || [],
          notes: product.notes || ""
        });
      }
      const fetchInitialData = async () => {
        try {
          const [catRes, unitRes] = await Promise.all([
            categoryService.listAll({ status: "ACTIVE" }),
            unitService.listAll({ status: "ACTIVE" })
          ]);
          setCategories(Array.isArray(catRes) ? catRes : catRes.data || []);
          setUnits(Array.isArray(unitRes) ? unitRes : unitRes.data || []);
        } catch (error) {
          console.error("Failed to fetch categories or units");
        }
      };
      fetchInitialData();
    }
  }, [isOpen, product]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // 1. Update Product Details
      const payload = {
        ...formData,
        purchase_price: parseNumberFromSeparator(formData.purchase_price),
        sell_price: parseNumberFromSeparator(formData.sell_price),
        options: product.options || []
      };
      if (!payload.category_id) payload.category_id = null;
      if (!payload.unit_id) payload.unit_id = null;
      
      await masterService.products.update(product.id, payload);

      showSuccess("Produk berhasil diperbarui");
      onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
      showError(error.message || "Gagal mengupdate produk");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 flex justify-between items-center bg-teal-600 text-white rounded-t-2xl">
          <h2 className="text-base font-semibold tracking-tight">Edit Data Produk</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-5">
          <div className="space-y-1">
            <label className="text-sm font-semibold text-slate-700">Nama Produk</label>
            <input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-1">
              <label className="text-sm font-semibold text-slate-700">Harga Modal</label>
              <input type="text" inputMode="numeric" name="purchase_price" value={formatNumberWithSeparator(formData.purchase_price)} onChange={(e) => setFormData(prev => ({ ...prev, purchase_price: e.target.value }))} className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors" />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-semibold text-slate-700">Harga Jual</label>
              <input type="text" inputMode="numeric" name="sell_price" value={formatNumberWithSeparator(formData.sell_price)} onChange={(e) => setFormData(prev => ({ ...prev, sell_price: e.target.value }))} className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-1">
              <label className="text-sm font-semibold text-slate-700">Kategori</label>
              <SearchableSelect
                name="category_id"
                value={formData.category_id}
                onChange={(val) => setFormData(prev => ({ ...prev, category_id: val }))}
                onAddNew={handleAddCategory}
                addNewLabel="Tambah Kategori Baru"
                options={categories}
                placeholder="Cari atau pilih Kategori..."
                required
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-semibold text-slate-700">Satuan</label>
              <SearchableSelect
                name="unit_id"
                value={formData.unit_id}
                onChange={(val) => setFormData(prev => ({ ...prev, unit_id: val }))}
                onAddNew={handleAddUnit}
                addNewLabel="Tambah Satuan Baru"
                options={units}
                placeholder="Cari atau pilih Satuan..."
              />
            </div>
          </div>

          {!isApps && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1">
                <label className="text-sm font-semibold text-slate-700">Barcode</label>
                <BarcodeScanInput
                  value={formData.barcode}
                  onChange={(val) => setFormData((prev) => ({ ...prev, barcode: val }))}
                  className="px-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 transition-colors"
                  focusClass="focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-semibold text-slate-700">SKU</label>
                <input type="text" name="sku" value={formData.sku} onChange={handleChange} className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors" />
              </div>
            </div>
          )}

          <div className="space-y-1">
            <label className="text-sm font-semibold text-slate-700">Status Produk</label>
            <SearchableSelect
              name="status"
              value={formData.status}
              onChange={(val) => setFormData(prev => ({ ...prev, status: val }))}
              options={[
                { id: 'ACTIVE', name: 'Aktif' },
                { id: 'INACTIVE', name: 'Non-Aktif' }
              ]}
              searchable={false}
              placeholder="Pilih Status..."
            />
          </div>

          {/* HIDDEN TEMPORARILY: Deskripsi
          <div className="space-y-1">
            <label className="text-sm font-semibold text-slate-700">Deskripsi</label>
            <textarea name="description" value={formData.description} onChange={handleChange} rows="4" className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"></textarea>
          </div>
          */}
          
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <button type="button" onClick={onClose} className="px-5 py-2 border border-slate-200 text-slate-600 font-semibold rounded-xl hover:bg-slate-50 transition-colors">Batal</button>
            <button type="submit" disabled={loading} className="px-5 py-2 bg-teal-600 text-white font-semibold rounded-xl hover:bg-teal-700 transition-colors disabled:opacity-50">
              {loading ? "Menyimpan..." : "Simpan Perubahan"}
            </button>
          </div>
        </form>
      </div>

    </div>
  );
}
